import { formatCurrency } from "@/lib/utils";
import { TrendingUp, Sparkles, Brain, DollarSign, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { SummaryStats } from "../types";

interface OverscopeDetectedCardProps {
    stats: SummaryStats;
    onViewOverscopedItems?: () => void;
}

export function OverscopeDetectedCard({ stats, onViewOverscopedItems }: OverscopeDetectedCardProps) {
    const additionalCommission = stats.insuranceOverscope * 0.1;
    const roiPercentage = stats.insuranceOverscope > 0 ? (additionalCommission / stats.insuranceOverscope) * 100 : 0;

    return (
        <Card className="border border-green-500/20 dark:border-green-500/20 bg-gradient-to-br from-green-50/20 to-green-50/10 dark:from-green-950/10 dark:to-green-950/5">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-green-500/80 to-emerald-500/80 rounded-lg">
                        <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <CardTitle className="text-base font-bold text-green-700 dark:text-green-300">TurboAI Carrier Overscope Detected</CardTitle>
                        <Badge variant="outline" className="text-xs mt-1 border-green-500/20 bg-green-50/20 dark:bg-green-900/10">
                            <Brain className="h-2.5 w-2.5 mr-1" />
                            Powered by TurboAI Intelligence
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/70 dark:bg-gray-900/50 rounded-lg p-2.5 border border-green-200/50 dark:border-green-800/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <DollarSign className="h-3.5 w-3.5 text-green-600" />
                            <p className="text-xs text-muted-foreground">Total Overscope</p>
                        </div>
                        <p className="text-lg font-bold text-green-700 dark:text-green-300">{formatCurrency(stats.insuranceOverscope)}</p>
                        <p className="text-xs text-muted-foreground">{stats.overscopeItemCount} items</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 rounded-lg p-2.5 border border-green-300/50 dark:border-green-700/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                            <p className="text-xs text-muted-foreground">Your Commission</p>
                        </div>
                        <p className="text-lg font-bold text-green-700 dark:text-green-300">{formatCurrency(additionalCommission)}</p>
                        <p className="text-xs font-semibold text-green-600">{roiPercentage.toFixed(0)}% ROI</p>
                    </div>
                </div>

                {/* Action button */}
                <Button
                    size="sm"
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                    onClick={onViewOverscopedItems}
                    data-testid="button-view-overscope"
                >
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Review Items
                </Button>
            </CardContent>
        </Card>
    );
}
